import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Logo } from "@/components/marketing/Logo";
import { LanguageSwitcher } from "@/components/marketing/LanguageSwitcher";

import { buildSeo } from "@/lib/seo";

// Version of the Terms and Privacy Policy a signup agrees to. Bump this when
// the documents change materially, so old acceptances stay distinguishable
// from new ones instead of silently appearing current.
const TERMS_VERSION = "2026-08";

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    // Only accept same-origin relative paths — reject anything that could redirect off-site.
    next:
      typeof search.next === "string" &&
      search.next.startsWith("/") &&
      !search.next.startsWith("//")
        ? search.next
        : undefined,
  }),
  head: () => {
    const seo = buildSeo({
      path: "/auth",
      title: "Sign in — Reality Twin Network",
      description:
        "Sign in or create your Reality Twin account. Build an AI twin of your company and start conversations with customers, partners, and investors.",
    });
    return { meta: seo.meta, links: seo.links };
  },
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const { t } = useTranslation();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // Signup-only gates. Checked before the request so a typo or a missing
  // consent never reaches Supabase as a half-created account.
  const passwordsMatch = password.length === 0 || password === password2;
  const signupReady =
    mode !== "signup" || (password.length >= 6 && passwordsMatch && acceptedTerms);

  const dest = next || "/dashboard";

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: dest });
    });
  }, [navigate, dest]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        if (password !== password2) {
          toast.error(
            t("app.auth.passwordMismatch", { defaultValue: "The passwords don't match." }),
          );
          return;
        }
        if (!acceptedTerms) {
          toast.error(
            t("app.auth.mustAcceptTerms", {
              defaultValue: "Please accept the Terms and the Privacy Policy to continue.",
            }),
          );
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/onboarding` },
        });
        if (error) throw error;
        // Supabase returns a user with an empty identities array when the email
        // already exists — surface that instead of a silent no-op.
        if (data.user && (data.user.identities?.length ?? 0) === 0) {
          toast.error(
            t("app.auth.alreadyRegistered", {
              defaultValue: "This email is already registered. Please sign in.",
            }),
          );
          setMode("signin");
          return;
        }
        // Record the consent. A checkbox that leaves no trace proves nothing,
        // so we store who agreed, when, and to which document version.
        // Best-effort: never block a successful signup on the audit write.
        if (data.user) {
          try {
            await (supabase as any).from("terms_acceptances").upsert(
              {
                user_id: data.user.id,
                accepted_terms: true,
                accepted_privacy: true,
                document_version: TERMS_VERSION,
                accepted_at: new Date().toISOString(),
              },
              { onConflict: "user_id" },
            );
          } catch {
            /* the account exists; the record can be reconciled later */
          }
        }
        if (data.session) {
          // Email confirmation is OFF → the user is signed in immediately.
          toast.success(t("app.auth.welcomeToast"));
          navigate({ to: "/onboarding", search: next ? ({ next } as any) : undefined });
        } else {
          // Email confirmation is ON → no session yet. Navigating into a
          // protected route would just bounce back to /auth, so tell the user
          // to confirm their email first.
          toast.success(
            t("app.auth.confirmEmailSent", {
              defaultValue: "Almost there — check your inbox to confirm your email, then sign in.",
            }),
          );
          setMode("signin");
          setPassword("");
          setPassword2("");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: dest });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("app.auth.authFailed"));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setLoading(true);
    // OAuth redirect_uri must stay on the public /auth page so the session can
    // hydrate before we navigate into a protected route. We stash `next` in the
    // URL so this component picks it up after the redirect.
    const redirect = new URL("/auth", window.location.origin);
    if (next) redirect.searchParams.set("next", next);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: redirect.toString(),
    });
    if (result.error) {
      toast.error(t("app.auth.googleFailed"));
      setLoading(false);
      return;
    }
    if (result.redirected) return;
    navigate({ to: dest });
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <Logo size={19} />
        <LanguageSwitcher />
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="w-full max-w-md rounded-[28px] border border-border bg-card p-8 sm:p-10 shadow-[0_18px_70px_-46px_oklch(0_0_0_/_0.35)]"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
            {mode === "signin" ? t("app.auth.signIn") : t("app.auth.signUp")}
          </p>
          <h1 className="mt-4 font-serif text-[40px] leading-[1.02] tracking-[-0.03em]">
            {mode === "signin" ? t("app.auth.welcomeBack") : t("app.auth.buildTwin")}
          </h1>
          <p className="mt-3 text-[15px] leading-[1.6] text-muted-foreground">
            {mode === "signin" ? t("app.auth.signInSub") : t("app.auth.signUpSub")}
          </p>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            className="mt-7 w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-background px-5 py-3 text-[14px] font-medium text-foreground hover:bg-cream transition disabled:opacity-60"
          >
            <GoogleIcon />
            {t("app.auth.continueGoogle")}
          </button>

          <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            {t("app.auth.or")}
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                {t("app.auth.email")}
              </label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-2xl border border-border bg-background px-4 py-3 text-[15px] outline-none placeholder:text-muted-foreground/55 focus:border-foreground/50 transition"
                placeholder={t("app.auth.emailPlaceholder")}
              />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                {t("app.auth.password")}
              </label>
              <input
                required
                type="password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-2xl border border-border bg-background px-4 py-3 text-[15px] outline-none placeholder:text-muted-foreground/55 focus:border-foreground/50 transition"
                placeholder="••••••••"
              />
            </div>

            {/* Signup only: confirm the password and agree to the documents. */}
            {mode === "signup" && (
              <>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    {t("app.auth.passwordConfirm", { defaultValue: "Confirm password" })}
                  </label>
                  <input
                    required
                    type="password"
                    minLength={6}
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    className={`mt-2 w-full rounded-2xl border bg-background px-4 py-3 text-[15px] outline-none placeholder:text-muted-foreground/55 transition ${
                      passwordsMatch
                        ? "border-border focus:border-foreground/50"
                        : "border-red-500/60 focus:border-red-500"
                    }`}
                    placeholder="••••••••"
                  />
                  {!passwordsMatch && (
                    <p className="mt-1.5 text-[12.5px] text-red-600">
                      {t("app.auth.passwordMismatch", {
                        defaultValue: "The passwords don't match.",
                      })}
                    </p>
                  )}
                </div>

                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-foreground"
                  />
                  <span className="text-[13px] leading-relaxed text-muted-foreground">
                    {t("app.auth.acceptPre", { defaultValue: "I accept the" })}{" "}
                    <Link to="/terms" className="underline text-foreground hover:opacity-80">
                      {t("app.auth.acceptTerms", { defaultValue: "Terms of Service" })}
                    </Link>{" "}
                    {t("app.auth.acceptAnd", { defaultValue: "and the" })}{" "}
                    <Link to="/privacy" className="underline text-foreground hover:opacity-80">
                      {t("app.auth.acceptPrivacy", { defaultValue: "Privacy Policy" })}
                    </Link>
                    .
                  </span>
                </label>
              </>
            )}

            <button
              type="submit"
              disabled={loading || !signupReady}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {mode === "signin" ? t("app.auth.submitSignIn") : t("app.auth.submitSignUp")}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-[14px] text-muted-foreground">
            {mode === "signin" ? t("app.auth.newHere") : t("app.auth.alreadyHave")}{" "}
            <button
              type="button"
              onClick={() => {
                setMode(mode === "signin" ? "signup" : "signin");
                setPassword2("");
                setAcceptedTerms(false);
              }}
              className="text-foreground font-medium hover:underline"
            >
              {mode === "signin" ? t("app.auth.switchToSignUp") : t("app.auth.switchToSignIn")}
            </button>
          </div>
          {mode === "signin" && (
            <div className="mt-3 text-center">
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}
